import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import type { EventClickArg, EventHoveringArg } from "@fullcalendar/core"
import type FullCalendar from "@fullcalendar/react"
import { useEventCallback } from "@mui/material"
import { type QueryFunctionContext, useQueryClient } from "@tanstack/react-query"

import { useResourceBookingDraftActions } from "../ResourceBookingDraftContext"
import { enqueueSnackbar } from "notistack"

import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { ConfigQuery, fetchFreeBusyScheduleForServiceCenter, FreeBusyScheduleResponse } from "../../../createOrderApi"
import { StandardTimelineCalendar } from "../calendar/StandardTimelineCalendar"
import { generateCalendarEvents } from "../calendar/helpers"
import { ResourceCalendarGroupContent } from "./ResourceCalendarGroupContent"
import { ResourceCalendarLabelContent } from "./ResourceCalendarLabelContent"
import { ResourceBookingPreviewImage } from "./ResourceBookingPreviewImage"
import { usePreviewImageDialog } from "./usePreviewImageDialog"
import { createNewEvent, DEFAULT_MINIMUM_DURATION_MINUTES, getBookingRangeFromAvailabilitySlot } from "./helpers"
import { type EventSourceEntry, memoizeByResponse, refreshEventSources, takeHandoff } from "./eventSourceSync"
import { EventTooltip } from "./tooltip/EventTooltip"
import {
    ResourceBase,
    TimelineCalendarEvent,
    TimelineCalendarResource,
    TimelineResourceGroupLabelContent,
    TimelineResourceLabelContent,
} from "../calendar/fullCalendarTypes"
import { useResourceDetails } from "../context/ResourceDetailsProvider"
import { useRefreshCalendar } from "../context/RefreshCalendarProvider"
import { useTimelineAutoRefresh } from "../context/useTimelineAutoRefresh"

const INTERVAL_MINUTES = 15

const transformData = (data: FreeBusyScheduleResponse) => {
    return data?.resources || []
}

type ServiceCenterFreeBusyQueryKey = Readonly<
    [string, Readonly<{ serviceCenterUid: string; startTime: string; endTime: string }>]
>
type WeeklySourceEntry = EventSourceEntry<ServiceCenterFreeBusyQueryKey, FreeBusyScheduleResponse>

const toWeeklySourceId = (serviceCenterUid: string) => `source-${serviceCenterUid}`

const WeeklyCalendarLoadingContext = createContext<ReadonlySet<string>>(new Set())

type WeeklyResourceLabelProps = {
    resource: TimelineResourceLabelContent["resource"]
    searchRegex: RegExp | undefined
    eventsLoadingNativeTitle: string
    handleOpenDetails: (id: string) => void
}

const WeeklyResourceLabel = memo(function WeeklyResourceLabel({
    resource,
    searchRegex,
    eventsLoadingNativeTitle,
    handleOpenDetails,
}: WeeklyResourceLabelProps) {
    const loadingUids = useContext(WeeklyCalendarLoadingContext)
    const isEventsLoading = loadingUids.has(resource.extendedProps.serviceCenterUid)
    return (
        <ResourceCalendarLabelContent
            resource={resource}
            searchRegex={searchRegex}
            handleOpenDetails={handleOpenDetails}
            isEventsLoading={isEventsLoading}
            eventsLoadingNativeTitle={eventsLoadingNativeTitle}
        />
    )
})

type WeeklyResourceBookingCalendarProps = {
    timelineResources: TimelineCalendarResource[]
    searchRegex: RegExp | undefined
    groupImagesMap: Map<string, string>
    handleBookClick: (event: TimelineCalendarEvent) => void
}

export const WeeklyResourceBookingCalendar = memo(function WeeklyResourceBookingCalendar({
    timelineResources,
    searchRegex,
    groupImagesMap,
    handleBookClick,
}: WeeklyResourceBookingCalendarProps) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const { selectedImage, openImageModal, closeImageModal } = usePreviewImageDialog()
    const { openResourceDetailsDialog } = useResourceDetails()

    const { setAndSyncDraftEvent } = useResourceBookingDraftActions()
    const { setCalendarRef, registerRefresher } = useRefreshCalendar()
    const { mutateAsync: callEventsMutation } = useApiMutation(fetchFreeBusyScheduleForServiceCenter, {})
    const queryClient = useQueryClient()
    const weeklySourcesRef = useRef<Record<string, WeeklySourceEntry>>({})
    const [hoveredSlotEl, setHoveredSlotEl] = useState<HTMLElement | null>(null)

    useTimelineAutoRefresh()

    const eventsLoadingNativeTitle = l10n.getString("create-order-resource-row-events-loading-tooltip")

    const [loadingUids, setLoadingUids] = useState<ReadonlySet<string>>(
        () => new Set(timelineResources.map((r) => r.serviceCenterUid)),
    )

    const loadingUidsRef = useRef(loadingUids)
    loadingUidsRef.current = loadingUids

    // already delivered slots for the range on screen — a refresh of these must not read as loading
    const loadedUidsRef = useRef<Set<string>>(new Set())

    useEffect(() => {
        loadedUidsRef.current = new Set()
        setLoadingUids(new Set(timelineResources.map((r) => r.serviceCenterUid)))
    }, [timelineResources])

    const calendarResourceByUid = useMemo(() => new Map(timelineResources.map((r) => [r.uid, r])), [timelineResources])

    const loadFreeBusy = useCallback(
        ({ queryKey: [_queryName, params] }: QueryFunctionContext<ServiceCenterFreeBusyQueryKey>) =>
            callEventsMutation({ ...params, availabilityViewInterval: INTERVAL_MINUTES }),
        [callEventsMutation],
    )

    const toSlotEvents = useMemo(
        () =>
            memoizeByResponse((response: FreeBusyScheduleResponse) =>
                generateCalendarEvents(
                    transformData(response).filter((slot) => calendarResourceByUid.has(slot.resource.uid)),
                    INTERVAL_MINUTES,
                    dateTimeFactory,
                    calendarResourceByUid,
                    DEFAULT_MINIMUM_DURATION_MINUTES,
                ),
            ),
        [dateTimeFactory, calendarResourceByUid],
    )

    const createEventSourceFetcher = useCallback(
        (serviceCenterUid: string) =>
            async (
                fetchInfo: { startStr: string; endStr: string },
                successCallback: (events: TimelineCalendarEvent[]) => void,
                failureCallback: (error: any) => void,
            ) => {
                if (!loadedUidsRef.current.has(serviceCenterUid)) {
                    setLoadingUids((prev) => (prev.has(serviceCenterUid) ? prev : new Set(prev).add(serviceCenterUid)))
                }
                try {
                    const queryKey: ServiceCenterFreeBusyQueryKey = [
                        ConfigQuery.SERVICE_CENTER_FREE_BUSY,
                        {
                            serviceCenterUid,
                            startTime: dateTimeFactory.toApiISOMinutesFormat(
                                dateTimeFactory.fromISO(fetchInfo.startStr),
                            ),
                            endTime: dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromISO(fetchInfo.endStr)),
                        },
                    ]
                    const sourceId = toWeeklySourceId(serviceCenterUid)
                    const handoff = takeHandoff(weeklySourcesRef.current[sourceId], queryKey)
                    const entry: WeeklySourceEntry = { queryKey }
                    weeklySourcesRef.current[sourceId] = entry

                    // bookings invalidate only the day queries
                    const response: FreeBusyScheduleResponse =
                        handoff ?? (await queryClient.fetchQuery({ queryKey, staleTime: 0, queryFn: loadFreeBusy }))
                    const events = toSlotEvents(response)
                    entry.renderedRaw = response

                    loadedUidsRef.current.add(serviceCenterUid)
                    successCallback(events)
                } catch (err) {
                    failureCallback(err)
                } finally {
                    setLoadingUids((prev) => {
                        if (!prev.has(serviceCenterUid)) return prev

                        const next = new Set(prev)
                        next.delete(serviceCenterUid)
                        return next
                    })
                }
            },
        [queryClient, dateTimeFactory, loadFreeBusy, toSlotEvents],
    )

    const refreshChangedSources = useCallback(
        (calendar: FullCalendar) => {
            void refreshEventSources({
                calendar: calendar.getApi(),
                queryClient,
                entries: weeklySourcesRef.current,
                queryFn: loadFreeBusy,
                toEvents: (_entry, response) => toSlotEvents(response),
            })
        },
        [queryClient, loadFreeBusy, toSlotEvents],
    )

    useEffect(() => registerRefresher(refreshChangedSources), [registerRefresher, refreshChangedSources])

    const eventSources = useMemo(() => {
        const uniqueServiceCenterUidList = [...new Set(timelineResources.map((r) => r.serviceCenterUid))]

        return uniqueServiceCenterUidList.map((uid) => ({
            id: toWeeklySourceId(uid),
            events: createEventSourceFetcher(uid),
        }))
    }, [timelineResources, createEventSourceFetcher])

    const handleEventHover = useCallback((info: EventHoveringArg | null) => setHoveredSlotEl(info?.el ?? null), [])

    const handleEventClick = useEventCallback((info: EventClickArg) => {
        setHoveredSlotEl(null)
        const event = info.event
        const resourceId = event._def.resourceIds?.[0]

        if (!resourceId) return

        const resource = timelineResources.find((res) => res.id === resourceId)
        if (resource?.accessControl?.hasAccess === false) return
        if (resource && loadingUidsRef.current.has(resource.serviceCenterUid)) {
            enqueueSnackbar({
                variant: "info",
                message: l10n.getString("create-order-resource-row-events-loading-tooltip"),
                autoHideDuration: 2000,
            })
            return
        }

        const slotStart = dateTimeFactory.fromISO(event.startStr)
        const slotEnd = dateTimeFactory.fromISO(event.endStr)
        const now = dateTimeFactory.now()

        const bookingRange = getBookingRangeFromAvailabilitySlot({
            dateTimeFactory,
            slotStart,
            slotEnd,
            now,
            intervalMinutes: INTERVAL_MINUTES,
            allDayStartHour: 8,
            maxDurationHours: 1,
        })

        if (!bookingRange) {
            enqueueSnackbar({
                variant: "warning",
                message: l10n.getString("create-order-past-booking-attempt-message"),
            })
            return
        }

        const { effectiveStart, adjustedEnd, originalEnd } = bookingRange

        const start = dateTimeFactory.toApiISOMinutesFormat(effectiveStart)
        const end = dateTimeFactory.toApiISOMinutesFormat(adjustedEnd)
        const timeRange = { start, end: dateTimeFactory.toApiISOMinutesFormat(originalEnd) }

        const newEvent = createNewEvent(resourceId, start, end, timeRange)
        const fromTimeline = resource ? [{ ...resource, id: Number(resource.id) }] : []

        setAndSyncDraftEvent(newEvent, fromTimeline)
        handleBookClick(newEvent)
    })

    const resourceLabelContent = useCallback(
        ({ resource }: TimelineResourceLabelContent) => (
            <WeeklyResourceLabel
                resource={resource}
                searchRegex={searchRegex}
                eventsLoadingNativeTitle={eventsLoadingNativeTitle}
                handleOpenDetails={openResourceDetailsDialog}
            />
        ),
        [searchRegex, eventsLoadingNativeTitle, openResourceDetailsDialog],
    )

    const resourceGroupLabelContent = useCallback(
        ({ groupValue }: TimelineResourceGroupLabelContent) => (
            <ResourceCalendarGroupContent
                title={groupValue}
                groupImagesMap={groupImagesMap}
                onImageClick={openImageModal}
            />
        ),
        [groupImagesMap, openImageModal],
    )

    const isResourceLaneLoading = useCallback(
        (extendedProps: ResourceBase) => loadingUids.has(extendedProps.serviceCenterUid),
        [loadingUids],
    )

    const handleDatesSet = useCallback(() => {
        loadedUidsRef.current = new Set()
        setLoadingUids(new Set(timelineResources.map((r) => r.serviceCenterUid)))
    }, [timelineResources])

    return (
        <WeeklyCalendarLoadingContext.Provider value={loadingUids}>
            <StandardTimelineCalendar
                ref={setCalendarRef}
                viewMode="weekGrid"
                eventSources={eventSources}
                resources={timelineResources}
                resourceLabelContent={resourceLabelContent}
                resourceGroupLabelContent={resourceGroupLabelContent}
                handleEventClick={handleEventClick}
                handleEventHover={handleEventHover}
                isResourceLaneLoading={isResourceLaneLoading}
                onDatesSet={handleDatesSet}
            />
            <EventTooltip anchorEl={hoveredSlotEl} />
            {selectedImage && (
                <ResourceBookingPreviewImage previewImage={selectedImage} handleClose={closeImageModal} />
            )}
        </WeeklyCalendarLoadingContext.Provider>
    )
})
